import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { factoryApi } from '../services/api';
import Header from '../components/layout/Header';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Package, Clock, AlertTriangle, Search, ChevronRight, Flame,
  CheckCircle2, CircleDot, Scale, Gem, Link2, Filter, ArrowUpDown,
  Download, Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';

// ─── Helpers ───
const fmtDate = (iso) => {
  if (!iso) return '';
  try { const d = new Date(iso); return `${d.getDate()} ${d.toLocaleString('en-IN',{month:'short'})}`; } catch { return ''; }
};

const fmtDeadline = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffH = Math.round((d - now) / 3600000);
    const diffD = Math.round((d - now) / 86400000);
    if (diffH < 0) return { text: `${Math.abs(diffH)}h overdue`, status: 'overdue' };
    if (diffH < 6) return { text: `${diffH}h left`, status: 'at_risk' };
    if (diffD <= 2) return { text: `${diffH}h left`, status: 'on_track' };
    return { text: `${fmtDate(iso)}`, status: 'on_track' };
  } catch { return null; }
};

const getStageCount = (order, stage) => {
  const sq = order.stage_quantities?.find(s => s.stage === stage);
  return sq?.quantity || 0;
};

const getProgress = (order) => {
  const dispatched = getStageCount(order, 'Received from Factory');
  return order.pieces_to_factory > 0 ? Math.round((dispatched / order.pieces_to_factory) * 100) : 0;
};

// ─── Material Dot ───
const MatDot = ({ received, label }) => {
  if (received === null) return null; // not needed
  return (
    <div className="flex items-center gap-1" data-testid={`mat-${label.toLowerCase()}`}>
      <span className={cn(
        "h-2.5 w-2.5 rounded-full flex-shrink-0",
        received ? "bg-emerald-500" : "bg-red-400 animate-pulse"
      )} />
      <span className="text-[10px] font-semibold text-muted-foreground">{label}</span>
    </div>
  );
};

// ─── Deadline Pill ───
const DeadlinePill = ({ iso, label, completed }) => {
  if (completed) return (
    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400 font-semibold">{label} Done</span>
  );
  const dl = fmtDeadline(iso);
  if (!dl) return <span className="text-[10px] text-muted-foreground">{label}: --</span>;
  const colors = {
    overdue: 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400',
    at_risk: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400',
    on_track: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400',
  };
  return (
    <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-semibold", colors[dl.status])}>
      {label}: {dl.text}
    </span>
  );
};

// ─── Pipeline Summary Bar ───
// Display label override: backend stage is still "Order Raised", but on the
// factory portal we surface it as "Order Accepted" — pieces on orders the
// factory has confirmed but hasn't started working on yet.
const STAGE_LABEL = {
  'Order Raised': 'Order Accepted',
};
const PipelineSummary = ({ orders }) => {
  const stages = useMemo(() => {
    // Split "Order Raised" pieces into two buckets:
    //  • Pending Acceptance — pieces sitting on orders the factory hasn't accepted yet.
    //  • Order Raised — pieces on accepted orders that work hasn't started on.
    const map = { 'Pending Acceptance': 0, 'Order Raised': 0, 'Casting': 0, 'On the Floor': 0, 'Received': 0 };
    orders.forEach(o => {
      const isPending = o.factory_acceptance?.status === 'pending';
      const isRejected = o.factory_acceptance?.status === 'rejected';
      const raised = o.order_raised_quantity || 0;
      if (isPending) {
        map['Pending Acceptance'] += raised;
      } else if (!isRejected) {
        map['Order Raised'] += raised;
        map['Casting'] += getStageCount(o, 'Casting');
        map['On the Floor'] += getStageCount(o, 'On the Floor');
        map['Received'] += getStageCount(o, 'Received from Factory');
      }
    });
    return map;
  }, [orders]);
  const colors = {
    'Pending Acceptance': 'bg-amber-500 dark:bg-amber-500',
    'Order Raised': 'bg-slate-200 dark:bg-slate-700',
    'Casting': 'bg-amber-300 dark:bg-amber-400/80',
    'On the Floor': 'bg-blue-400 dark:bg-blue-500',
    'Received': 'bg-emerald-400 dark:bg-emerald-500',
  };
  const total = Object.values(stages).reduce((a,b) => a+b, 0) || 1;

  return (
    <div data-testid="pipeline-summary">
      <div className="flex rounded-lg overflow-hidden h-3 mb-2">
        {Object.entries(stages).map(([stage, count]) => count > 0 ? (
          <div key={stage} className={cn("transition-all", colors[stage])} style={{ width: `${(count/total)*100}%` }} />
        ) : null)}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {Object.entries(stages).map(([stage, count]) => (
          <div key={stage} className="flex items-center gap-1.5">
            <span className={cn("h-2 w-2 rounded-full", colors[stage])} />
            <span className="text-[11px] text-muted-foreground">{STAGE_LABEL[stage] || stage}</span>
            <span className="text-[11px] font-bold tabular-nums">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Needs Attention Card ───
const AttentionCard = ({ order, navigate }) => {
  const castDl = fmtDeadline(order.casting_deadline);
  const delDl = fmtDeadline(order.delivery_deadline);
  const isOverdue = castDl?.status === 'overdue' || delDl?.status === 'overdue';
  const isAtRisk = castDl?.status === 'at_risk' || delDl?.status === 'at_risk';

  let reason = '';
  if (isOverdue) reason = castDl?.status === 'overdue' ? `Casting ${castDl.text}` : `Deadline ${delDl.text}`;
  else if (isAtRisk) reason = castDl?.status === 'at_risk' ? `Casting ${castDl.text}` : `Deadline ${delDl.text}`;
  else if (order.gold_received && !order.casting_completed && getStageCount(order, 'Casting') === 0 && (order.order_raised_quantity || 0) > 0)
    reason = 'Gold received — start casting';
  else return null;

  return (
    <div
      className={cn(
        "p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-all",
        isOverdue ? "bg-red-50 border-red-200 dark:bg-red-500/5 dark:border-red-500/20" :
        isAtRisk ? "bg-amber-50 border-amber-200 dark:bg-amber-500/5 dark:border-amber-500/20" :
        "bg-cyan-50 border-cyan-200 dark:bg-cyan-500/5 dark:border-cyan-500/20"
      )}
      onClick={() => navigate(`/factory/orders/${order.id}`)}
      data-testid={`attention-${order.order_number}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {isOverdue ? <Flame className="h-3.5 w-3.5 text-red-500 flex-shrink-0" /> :
           isAtRisk ? <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" /> :
           <CircleDot className="h-3.5 w-3.5 text-cyan-500 flex-shrink-0" />}
          <span className="text-sm font-bold truncate">{order.order_number}</span>
          <span className="text-xs text-muted-foreground">{order.pieces_to_factory} pcs</span>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      </div>
      <p className={cn("text-xs mt-1 ml-5.5 font-medium",
        isOverdue ? "text-red-600 dark:text-red-400" :
        isAtRisk ? "text-amber-600 dark:text-amber-400" :
        "text-cyan-600 dark:text-cyan-400"
      )}>{reason}</p>
    </div>
  );
};

// ─── Order Row ───
const OrderRow = ({ order, navigate }) => {
  const pct = getProgress(order);
  const dispatched = getStageCount(order, 'Received from Factory');
  const fa = order.factory_acceptance || { status: 'accepted' };
  const isPending = fa.status === 'pending';
  const isRejected = fa.status === 'rejected';

  return (
    <div
      className={cn(
        "border-b border-border transition-colors cursor-pointer group",
        isPending ? "bg-amber-50/70 hover:bg-amber-100/60 dark:bg-amber-500/10 dark:hover:bg-amber-500/15 border-l-4 border-l-amber-500" :
        isRejected ? "bg-red-50/70 hover:bg-red-100/60 dark:bg-red-500/10 dark:hover:bg-red-500/15 border-l-4 border-l-red-500" :
        "hover:bg-muted/30"
      )}
      onClick={() => navigate(`/factory/orders/${order.id}`)}
      data-testid={`factory-order-${order.id}`}
    >
      <div className="grid grid-cols-[1.1fr_50px_0.9fr_90px_1.4fr_110px_28px] gap-2 items-center px-4 py-3">
        {/* CO Number + Priority + Acceptance pill */}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-bold text-foreground truncate">{order.order_number}</span>
            {isPending && (
              <Badge
                className="text-[9px] px-1.5 py-0 h-4 bg-amber-500 text-white border-0 animate-pulse"
                data-testid={`order-pending-acceptance-${order.id}`}
              >
                ACCEPT REQUIRED
              </Badge>
            )}
            {isRejected && (
              <Badge
                className="text-[9px] px-1.5 py-0 h-4 bg-red-500 text-white border-0"
                data-testid={`order-rejected-${order.id}`}
              >
                REJECTED
              </Badge>
            )}
            {!isPending && !isRejected && order.priority === 'Urgent' && <Badge variant="destructive" className="text-[9px] px-1.5 py-0 h-4">URGENT</Badge>}
            {!isPending && !isRejected && order.priority === 'High' && <Badge className="text-[9px] px-1.5 py-0 h-4 bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">HIGH</Badge>}
            {order.manufacturing_form ? (
              <Badge
                onClick={(e) => {
                  e.stopPropagation();
                  const token = encodeURIComponent(localStorage.getItem('siddhi_token') || '');
                  window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/manufacturing-form/file?token=${token}`, '_blank', 'noopener');
                }}
                className="text-[9px] px-1.5 py-0 h-4 bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/40 hover:bg-emerald-200 dark:hover:bg-emerald-500/25 cursor-pointer"
                title={`Manufacturing Order Form: ${order.manufacturing_form.filename || 'form'} — click to download`}
                data-testid={`factory-order-mfg-form-${order.id}`}
              >
                MFG ✓
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="text-[9px] px-1.5 py-0 h-4 text-muted-foreground border-dashed"
                title="No Manufacturing Order Form on file."
                data-testid={`factory-order-mfg-form-missing-${order.id}`}
              >
                No MFG
              </Badge>
            )}
            {order.factory_processing_sheet ? (
              <Badge
                onClick={(e) => {
                  e.stopPropagation();
                  const token = encodeURIComponent(localStorage.getItem('siddhi_token') || '');
                  window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/factory-processing-sheet/file?token=${token}`, '_blank', 'noopener');
                }}
                className="text-[9px] px-1.5 py-0 h-4 bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/40 hover:bg-emerald-200 dark:hover:bg-emerald-500/25 cursor-pointer"
                title={`Factory Processing Sheet: ${order.factory_processing_sheet.filename || 'sheet'} — click to download`}
                data-testid={`factory-order-sheet-${order.id}`}
              >
                Sheet ✓
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="text-[9px] px-1.5 py-0 h-4 text-muted-foreground border-dashed"
                title="No Factory Processing Sheet on file."
                data-testid={`factory-order-sheet-missing-${order.id}`}
              >
                No sheet
              </Badge>
            )}
          </div>
          {isPending && (
            <span className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">
              Tap to review &amp; accept
            </span>
          )}
          {isRejected && (
            <span className="text-[10px] text-red-600 dark:text-red-400 font-semibold truncate block">
              {fa.reject_reason ? `Rejected: ${fa.reject_reason}` : 'Rejected — awaiting admin'}
            </span>
          )}
          {!isPending && !isRejected && order.has_open_requests && (
            <span className="text-[10px] text-red-500 font-medium">Open request</span>
          )}
        </div>

        {/* Pieces */}
        <span className="text-sm font-bold tabular-nums text-center">{order.pieces_to_factory}</span>

        {/* Materials Received */}
        <div className="flex items-center gap-3">
          <MatDot received={order.gold_received} label="Gold" />
          <MatDot received={order.diamonds_received} label="Dia" />
          <MatDot received={order.chains_received} label="Chain" />
        </div>

        {/* Casting Status */}
        <div>
          <DeadlinePill iso={order.casting_deadline} label="Cast" completed={order.casting_completed} />
        </div>

        {/* Current Status - piece breakup */}
        <div className="flex flex-wrap gap-x-2 gap-y-0.5">
          {(() => {
            const stages = [
              { key: 'Order Raised', label: 'Accepted', qty: order.order_raised_quantity || 0, color: 'text-slate-500' },
              { key: 'Casting', label: 'Cast', qty: getStageCount(order, 'Casting'), color: 'text-amber-600 dark:text-amber-400' },
              { key: 'On the Floor', label: 'Floor', qty: getStageCount(order, 'On the Floor'), color: 'text-blue-600 dark:text-blue-400' },
              { key: 'Received', label: 'Done', qty: dispatched, color: 'text-emerald-600 dark:text-emerald-400' },
            ];
            return stages.filter(s => s.qty > 0).map(s => (
              <span key={s.key} className={cn("text-[11px] font-semibold tabular-nums", s.color)}>
                {s.qty} {s.label}
              </span>
            ));
          })()}
        </div>

        {/* Factory Deadline - prominent */}
        <div className="text-right">
          {order.delivery_completed ? (
            <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">Done</span>
          ) : order.delivery_deadline ? (() => {
            const dl = fmtDeadline(order.delivery_deadline);
            if (!dl) return <span className="text-sm text-muted-foreground">--</span>;
            const colors = {
              overdue: 'text-red-600 dark:text-red-400',
              at_risk: 'text-amber-600 dark:text-amber-400',
              on_track: 'text-foreground',
            };
            return (
              <div>
                <p className={cn("text-base font-extrabold tabular-nums leading-tight", colors[dl.status])}>{dl.text}</p>
                <p className="text-[10px] text-muted-foreground">{fmtDate(order.delivery_deadline)}</p>
              </div>
            );
          })() : <span className="text-sm text-muted-foreground/50">Not set</span>}
        </div>

        {/* Arrow */}
        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  );
};

// ─── Live Status Grid ───
// Excel-like cell grid: rows = COs, columns = 9 factory stages, cells = piece counts.
// Backend storage uses 'Order Raised' / 'Received from Factory'; factory terminology
// labels them 'Order Accepted' / 'Dispatched'.
const LIVE_STAGES = [
  { label: 'Order Accepted', key: 'Order Raised',          tone: 'slate' },
  { label: 'Casting',        key: 'Casting',               tone: 'amber' },
  { label: 'On the Floor',   key: 'On the Floor',          tone: 'blue' },
  { label: 'Filing',         key: 'Filing',                tone: 'indigo', isSub: true },
  { label: 'Pre Polish',     key: 'Pre Polish',            tone: 'violet', isSub: true },
  { label: 'Setting',        key: 'Setting',               tone: 'pink',   isSub: true },
  { label: 'Polishing',      key: 'Polishing',             tone: 'cyan',   isSub: true },
  { label: 'Finished Goods', key: 'Finished Goods',        tone: 'teal',   isSub: true },
  { label: 'Dispatched',     key: 'Received from Factory', tone: 'emerald' },
];

const cellCount = (order, key) => {
  if (['Filing','Pre Polish','Setting','Polishing','Finished Goods'].includes(key)) {
    const r = (order.factory_sub_stages || []).find(s => s.stage === key);
    return r?.quantity || 0;
  }
  if (key === 'Order Raised') return order.order_raised_quantity || 0;
  const r = (order.stage_quantities || []).find(s => s.stage === key);
  return r?.quantity || 0;
};

const LiveStatusGrid = ({ orders, navigate }) => {
  const [hideDispatched, setHideDispatched] = useState(true);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const rows = useMemo(() => {
    let list = orders;
    if (hideDispatched) list = list.filter(o => o.status !== 'Dispatched');
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o => (o.order_number || '').toLowerCase().includes(q));
    }
    return list;
  }, [orders, hideDispatched, search]);

  const stageTotals = useMemo(() => {
    const totals = LIVE_STAGES.map(() => 0);
    let pcs = 0;
    rows.forEach(o => {
      pcs += o.pieces_to_factory || 0;
      LIVE_STAGES.forEach((s, i) => { totals[i] += cellCount(o, s.key); });
    });
    return { stages: totals, pieces: pcs, grand: totals.reduce((a, b) => a + b, 0) };
  }, [rows]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const { data, headers } = await factoryApi.exportLiveStatusXlsx(!hideDispatched);
      const cd = headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      const fname = m ? m[1] : `LiveStatus_${Date.now()}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Live status exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card data-testid="live-status-grid">
      <CardContent className="p-4 space-y-3">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search CO..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 w-[200px]"
                data-testid="live-status-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="hide-dispatched"
                checked={hideDispatched}
                onCheckedChange={setHideDispatched}
                data-testid="hide-dispatched-toggle"
              />
              <Label htmlFor="hide-dispatched" className="text-xs font-medium cursor-pointer select-none">
                Hide dispatched
              </Label>
            </div>
            <Badge variant="outline" className="text-[10px]">
              {rows.length} order{rows.length === 1 ? '' : 's'}
            </Badge>
          </div>
          <Button
            size="sm"
            onClick={handleExport}
            disabled={exporting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="live-status-export-btn"
          >
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            Export Excel
          </Button>
        </div>

        {/* Grid */}
        <div className="overflow-auto rounded-md border border-border max-h-[70vh]">
          <table className="w-full text-xs border-collapse" data-testid="live-status-table">
            <thead className="sticky top-0 z-20 bg-slate-900 text-white">
              <tr>
                <th className="sticky left-0 z-30 bg-slate-900 text-left px-3 py-2 border-r border-slate-700 min-w-[160px]">CO Number</th>
                <th className="px-2 py-2 border-r border-slate-700 text-center">Pcs</th>
                {LIVE_STAGES.map(s => (
                  <th key={s.key} className="px-2 py-2 border-r border-slate-700 text-center min-w-[88px]">
                    <span className="whitespace-nowrap">{s.label}</span>
                  </th>
                ))}
                <th className="px-2 py-2 text-center bg-amber-700 min-w-[70px]">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={LIVE_STAGES.length + 3} className="px-3 py-8 text-center text-muted-foreground">
                    No orders to display
                  </td>
                </tr>
              ) : rows.map((o, idx) => {
                const counts = LIVE_STAGES.map(s => cellCount(o, s.key));
                const rowTotal = counts.reduce((a, b) => a + b, 0);
                return (
                  <tr
                    key={o.id}
                    onClick={() => navigate(`/factory/orders/${o.id}`)}
                    className={cn(
                      "cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-500/5 transition-colors",
                      idx % 2 === 1 && "bg-muted/30"
                    )}
                    data-testid={`live-status-row-${o.id}`}
                  >
                    <td className="sticky left-0 z-10 bg-inherit px-3 py-2 border-r border-border font-semibold text-foreground">
                      <div className="flex items-center gap-2">
                        <span className="truncate">{o.order_number}</span>
                        {o.priority === 'Urgent' && <span className="text-[9px] px-1 py-0 rounded bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300 font-bold">U</span>}
                        {o.priority === 'High' && <span className="text-[9px] px-1 py-0 rounded bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 font-bold">H</span>}
                      </div>
                    </td>
                    <td className="px-2 py-2 border-r border-border text-center tabular-nums font-semibold">{o.pieces_to_factory || 0}</td>
                    {counts.map((c, i) => (
                      <td
                        key={LIVE_STAGES[i].key}
                        className={cn(
                          "px-2 py-2 border-r border-border text-center tabular-nums",
                          c > 0 ? "font-bold text-foreground" : "text-muted-foreground/40"
                        )}
                      >
                        {c}
                      </td>
                    ))}
                    <td className="px-2 py-2 text-center tabular-nums font-bold bg-amber-50/60 dark:bg-amber-500/10">{rowTotal}</td>
                  </tr>
                );
              })}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="sticky bottom-0 z-20 bg-amber-100 dark:bg-amber-500/20">
                <tr className="font-bold">
                  <td className="sticky left-0 z-30 bg-amber-100 dark:bg-amber-500/20 px-3 py-2 border-r border-amber-300 dark:border-amber-500/40">TOTAL</td>
                  <td className="px-2 py-2 border-r border-amber-300 dark:border-amber-500/40 text-center tabular-nums">{stageTotals.pieces}</td>
                  {stageTotals.stages.map((t, i) => (
                    <td key={LIVE_STAGES[i].key} className="px-2 py-2 border-r border-amber-300 dark:border-amber-500/40 text-center tabular-nums">{t}</td>
                  ))}
                  <td className="px-2 py-2 text-center tabular-nums bg-amber-200 dark:bg-amber-500/30">{stageTotals.grand}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Main Component ───
const FactoryDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('priority'); // priority, due_date, progress

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await factoryApi.getOrders();
      setOrders(data);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Filter out archived, customer-dispatched, fully factory-dispatched,
  // inventory-only (no factory work), and admin-dismissed orders.
  const activeOrders = useMemo(() =>
    orders.filter(o => {
      if (o.archived || o.status === 'Dispatched') return false;
      if (o.factory_dismissed) return false;
      // Inventory-only orders never need factory action — never show them.
      if ((o.pieces_to_factory || 0) <= 0) return false;
      const received = o.stage_quantities?.find(s => s.stage === 'Received from Factory')?.quantity || 0;
      if (o.pieces_to_factory > 0 && received >= o.pieces_to_factory) return false;
      return true;
    }),
  [orders]);

  // Search + filter
  const filtered = useMemo(() => {
    let list = activeOrders;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o => o.order_number?.toLowerCase().includes(q));
    }
    if (priorityFilter !== 'all') {
      list = list.filter(o => o.priority === priorityFilter);
    }
    // Sort
    list = [...list].sort((a, b) => {
      // Always pin pending-acceptance orders to the very top — they block all other work.
      const aPending = a.factory_acceptance?.status === 'pending' ? 0 : 1;
      const bPending = b.factory_acceptance?.status === 'pending' ? 0 : 1;
      if (aPending !== bPending) return aPending - bPending;
      if (sortBy === 'priority') {
        const p = { Urgent: 0, High: 1, Normal: 2 };
        return (p[a.priority] ?? 2) - (p[b.priority] ?? 2);
      }
      if (sortBy === 'due_date') {
        const da = a.delivery_deadline ? new Date(a.delivery_deadline).getTime() : Infinity;
        const db_ = b.delivery_deadline ? new Date(b.delivery_deadline).getTime() : Infinity;
        return da - db_;
      }
      if (sortBy === 'progress') return getProgress(a) - getProgress(b);
      return 0;
    });
    return list;
  }, [activeOrders, search, priorityFilter, sortBy]);

  // Needs attention
  const attentionOrders = useMemo(() => {
    return activeOrders.filter(o => {
      const castDl = fmtDeadline(o.casting_deadline);
      const delDl = fmtDeadline(o.delivery_deadline);
      if (castDl?.status === 'overdue' || castDl?.status === 'at_risk') return true;
      if (delDl?.status === 'overdue' || delDl?.status === 'at_risk') return true;
      if (o.gold_received && !o.casting_completed && getStageCount(o, 'Casting') === 0 && (o.order_raised_quantity || 0) > 0) return true;
      return false;
    }).slice(0, 5);
  }, [activeOrders]);

  // KPIs
  // WIP = pieces currently being worked on by the factory:
  //   sum of (Order Raised + Casting + On the Floor) across ACCEPTED orders.
  // Pieces in later stages (Filing → Finished Goods → Dispatched) are excluded
  // because they're considered "done" or "ready to dispatch back".
  // Pending-acceptance and rejected orders are excluded entirely.
  const kpis = useMemo(() => {
    let wip = 0;
    let pendingPieces = 0;
    activeOrders.forEach(o => {
      const status = o.factory_acceptance?.status;
      if (status === 'pending') {
        pendingPieces += (o.pieces_to_factory || 0);
        return;
      }
      if (status === 'rejected') return;
      // accepted (or legacy = accepted)
      // Order Raised qty lives at the top level on factory orders (not inside stage_quantities).
      wip += (o.order_raised_quantity || 0)
           + getStageCount(o, 'Casting')
           + getStageCount(o, 'On the Floor');
    });
    const urgent = activeOrders.filter(o => o.priority === 'Urgent').length;
    const overdue = activeOrders.filter(o => {
      const c = fmtDeadline(o.casting_deadline);
      const d = fmtDeadline(o.delivery_deadline);
      return c?.status === 'overdue' || d?.status === 'overdue';
    }).length;
    const pendingAcceptance = activeOrders.filter(o => o.factory_acceptance?.status === 'pending').length;
    return { total: activeOrders.length, wip, pendingPieces, urgent, overdue, pendingAcceptance };
  }, [activeOrders]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header title="Factory Portal" />
        <div className="p-5 space-y-4 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
          </div>
          <Skeleton className="h-10 rounded-xl" />
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-14 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="factory-dashboard">
      <Header title="Factory Portal" onRefresh={fetchOrders} />

      <div className="p-4 md:p-6 space-y-5 max-w-7xl mx-auto">
        {/* Pending Acceptance Banner — only when there is at least one new order
            awaiting factory acceptance. Tappable rows are highlighted below. */}
        {kpis.pendingAcceptance > 0 && (
          <div
            className="rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-500/10 dark:to-amber-500/5 dark:border-amber-500/40 p-4 flex items-center justify-between gap-3"
            data-testid="pending-acceptance-banner"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0 animate-pulse">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-amber-900 dark:text-amber-200">
                  {kpis.pendingAcceptance} new order{kpis.pendingAcceptance === 1 ? '' : 's'} awaiting your acceptance
                </p>
                <p className="text-[11px] text-amber-700 dark:text-amber-300">
                  Review the brief, then Accept or Reject — you cannot update these orders until accepted.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Active Orders', value: kpis.total, icon: Package, color: 'blue' },
            { label: 'WIP Pieces', value: kpis.wip, icon: Scale, color: 'amber' },
            { label: 'Pending Acceptance', value: kpis.pendingPieces, icon: Clock, color: kpis.pendingPieces > 0 ? 'amber' : 'slate', hint: `${kpis.pendingAcceptance} order${kpis.pendingAcceptance === 1 ? '' : 's'}` },
            { label: 'Urgent', value: kpis.urgent, icon: AlertTriangle, color: 'red' },
            { label: 'Overdue', value: kpis.overdue, icon: Flame, color: kpis.overdue > 0 ? 'red' : 'emerald' },
          ].map(kpi => {
            const Icon = kpi.icon;
            return (
              <Card key={kpi.label} data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g,'-')}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center",
                      `bg-${kpi.color}-100 dark:bg-${kpi.color}-500/20`
                    )}>
                      <Icon className={cn("h-5 w-5", `text-${kpi.color}-600 dark:text-${kpi.color}-400`)} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold tabular-nums">{kpi.value}</p>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{kpi.label}</p>
                      {kpi.hint && (
                        <p className="text-[10px] text-muted-foreground/80 mt-0.5 truncate">{kpi.hint}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Pipeline Summary */}
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Pipeline Overview</p>
            <PipelineSummary orders={activeOrders} />
          </CardContent>
        </Card>

        {/* Needs Attention */}
        {attentionOrders.length > 0 && (
          <div data-testid="needs-attention-section">
            <h2 className="font-heading text-sm font-bold text-foreground mb-2 flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Needs Attention
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {attentionOrders.map(o => (
                <AttentionCard key={o.id} order={o} navigate={navigate} />
              ))}
            </div>
          </div>
        )}

        {/* Tabs: Orders (default) | Live Status grid */}
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="live-status" data-testid="tab-live-status">Live Status</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="space-y-3 mt-3">
            {/* Search + Filters */}
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by CO number..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-orders"
                />
              </div>
              <div className="flex gap-2">
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-[130px]" data-testid="filter-priority">
                    <Filter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Normal">Normal</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[130px]" data-testid="sort-orders">
                    <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="priority">Priority</SelectItem>
                    <SelectItem value="due_date">Due Date</SelectItem>
                    <SelectItem value="progress">Progress</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Orders List */}
            {filtered.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Package className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm font-medium text-muted-foreground">
                    {search || priorityFilter !== 'all' ? 'No orders match your filters' : 'No active orders assigned'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-[1.1fr_50px_0.9fr_90px_1.4fr_110px_28px] gap-2 items-center px-4 py-2.5 bg-muted/50 border-b border-border text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <span>Order</span>
                  <span className="text-center">Pcs</span>
                  <span>Materials</span>
                  <span>Casting</span>
                  <span>Status</span>
                  <span className="text-right">Deadline</span>
                  <span></span>
                </div>
                {filtered.map(order => (
                  <OrderRow key={order.id} order={order} navigate={navigate} />
                ))}
              </Card>
            )}

            {/* Order count */}
            <p className="text-xs text-muted-foreground text-center">
              Showing {filtered.length} of {activeOrders.length} active orders
            </p>
          </TabsContent>

          <TabsContent value="live-status" className="mt-3">
            <LiveStatusGrid orders={orders} navigate={navigate} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default FactoryDashboard;
